import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Phone, Shield, Clock, Award, Users, MapPin, Star, ArrowRight, ChevronRight, Menu, X, Scale, LogIn, FileText } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from '@studio-freight/lenis';

gsap.registerPlugin(ScrollTrigger);

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Scroll-triggered reveal ─────────────────────────────── */
const Reveal = ({ children, delay = 0, direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const variants = {
    hidden: {
      opacity: 0,
      y: direction === 'up' ? 50 : direction === 'down' ? -50 : 0,
      x: direction === 'left' ? 50 : direction === 'right' ? -50 : 0,
      scale: 0.97,
    },
    visible: { opacity: 1, y: 0, x: 0, scale: 1 },
  };
  return (
    <motion.div ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'}
      variants={variants} transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.div>
  );
};

/* ── Animated counter ────────────────────────────────────── */
const Counter = ({ value, suffix = '', prefix = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(prefix + '0' + suffix);

  useEffect(() => {
    if (!isInView) return;
    const numericPart = value.replace(/[^0-9.]/g, '');
    if (!numericPart) { setDisplay(value); return; }
    const target = parseFloat(numericPart);
    const duration = 2000;
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(target * eased);
      setDisplay(prefix + current + suffix);
      if (progress < 1) requestAnimationFrame(animate);
      else setDisplay(value);
    };
    requestAnimationFrame(animate);
  }, [isInView, value, suffix, prefix]);

  return <span ref={ref} className="counter-value">{display}</span>;
};

/* ── Floating particles ──────────────────────────────────── */
const Particles = () => {
  const particles = useMemo(() => Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 18 + 8,
    delay: Math.random() * 12,
    opacity: Math.random() * 0.5 + 0.1,
  })), []);
  return (
    <div className="hero__particles">
      {particles.map(p => (
        <div key={p.id} className="particle" style={{
          left: p.left, width: p.size, height: p.size,
          animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s`,
        }} />
      ))}
    </div>
  );
};

/* ── Infinite Marquee ────────────────────────────────────── */
const Marquee = ({ items }) => (
  <div className="marquee-wrap">
    <div className="marquee-track">
      {[...items, ...items].map((text, i) => (
        <span className="marquee-item" key={i}>
          <span className="marquee-item__dot" />
          {text}
        </span>
      ))}
    </div>
  </div>
);

/* ── JSON-LD ─────────────────────────────────────────────── */
const StructuredData = () => {
  const data = {
    "@context": "https://schema.org", "@type": "LocalBusiness",
    "name": "Lone Star Bonding Co.",
    "description": "Houston's most trusted 24/7 bail bond service. Licensed bondsman Steve Cruz has served Harris County for over 30 years.",
    "url": "https://lonestarbonding.com", "telephone": "+1-713-228-8800",
    "address": { "@type": "PostalAddress", "streetAddress": "1420 Washington Ave", "addressLocality": "Houston", "addressRegion": "TX", "postalCode": "77002", "addressCountry": "US" },
    "geo": { "@type": "GeoCoordinates", "latitude": "29.7604", "longitude": "-95.3698" },
    "openingHours": "Mo-Su 00:00-23:59", "priceRange": "$$",
    "image": "https://customer-assets.emergentagent.com/job_bond-app-hub/artifacts/95cp39mf_LONESTAR_BONDINGLOGO2.jpg",
    "areaServed": [{ "@type": "City", "name": "Houston" }, { "@type": "AdministrativeArea", "name": "Harris County" }, { "@type": "AdministrativeArea", "name": "Fort Bend County" }],
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "312" }
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
};

/* ══════════════════════════════════════════════════════════
   HOMEPAGE
   ══════════════════════════════════════════════════════════ */
const CinematicHome = () => {
  const { lang, switchLang } = useLanguage();
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const heroRef = useRef(null);
  const gsapScopeRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  /* Lenis smooth scroll */
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.08, smoothWheel: true });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    return () => { lenis.destroy(); gsap.ticker.remove(lenis.raf); };
  }, []);

  /* GSAP ScrollTrigger animations */
  useEffect(() => {
    if (!gsapScopeRef.current) return;
    const ctx = gsap.context(() => {
      // Glow dividers animate on scroll
      gsap.utils.toArray('.glow-divider').forEach(el => {
        gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 1, scrollTrigger: { trigger: el, start: 'top 85%' } });
      });
      // Authority cards stagger
      gsap.utils.toArray('.authority-card').forEach((card, i) => {
        gsap.fromTo(card, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.8, delay: i * 0.08,
          scrollTrigger: { trigger: card, start: 'top 88%' }
        });
      });
      // Testimonial cards
      gsap.utils.toArray('.testimonial-card').forEach((card, i) => {
        gsap.fromTo(card, { y: 50, opacity: 0, scale: 0.95 }, {
          y: 0, opacity: 1, scale: 1, duration: 0.9, delay: i * 0.1,
          scrollTrigger: { trigger: card, start: 'top 90%' }
        });
      });
    }, gsapScopeRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const trackEvent = useCallback((eventType) => {
    fetch(`${API}/api/track`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_type: eventType, page: 'home' })
    }).catch(() => {});
  }, []);

  const serviceAreas = [
    { name: 'Downtown Houston / Harris County Jail', zip: '77002', desc: lang === 'es' ? 'Servicio inmediato en la Carcel del Condado de Harris, 1200 Baker St.' : 'Immediate service at Harris County Jail, 1200 Baker St. Fastest release times.' },
    { name: 'Sunnyside / South Houston', zip: '77033', desc: lang === 'es' ? 'Sirviendo Sunnyside, South Park y MacGregor con servicios de fianzas 24/7.' : 'Serving Sunnyside, South Park & MacGregor with 24/7 bail bond services.' },
    { name: 'Fifth Ward / Kashmere Gardens', zip: '77026', desc: lang === 'es' ? 'Fianzas rapidas para Fifth Ward, Kashmere Gardens y comunidades circundantes.' : 'Fast bail bonds for Fifth Ward, Kashmere Gardens & surrounding communities.' },
    { name: 'Sharpstown / Southwest Houston', zip: '77036', desc: lang === 'es' ? 'Servicio bilingue completo para Sharpstown, Gulfton y Westwood.' : 'Full bilingual service for Sharpstown, Gulfton & Westwood residents.' },
    { name: 'Northside / East End', zip: '77009', desc: lang === 'es' ? 'Cobertura rapida para Northside Village, East End y Magnolia Park.' : 'Quick coverage for Northside Village, East End & Magnolia Park.' },
    { name: 'Greenspoint / North Houston', zip: '77060', desc: lang === 'es' ? 'Sirviendo Greenspoint, Aldine y comunidades del norte de Houston.' : 'Serving Greenspoint, Aldine & North Houston communities.' },
  ];

  const testimonials = [
    { text: lang === 'es' ? 'Llamamos a las 2 de la manana y Steve contesto al primer timbre. Mi esposo fue liberado en horas. No puedo agradecer suficiente a Lone Star Bonding.' : 'We called at 2 AM and Steve picked up on the first ring. My husband was released within hours. Cannot thank Lone Star Bonding enough.',
      name: 'Maria G.', role: 'Houston, TX', initials: 'MG' },
    { text: lang === 'es' ? 'Despues de llamar a 3 fiadores diferentes, Lone Star fue el unico que contesto inmediatamente y explico todo en espanol. Profesionales de verdad.' : 'After calling 3 different bondsmen, Lone Star was the only one who answered immediately and explained everything clearly. True professionals.',
      name: 'Robert T.', role: 'Sunnyside, TX', initials: 'RT' },
    { text: lang === 'es' ? 'Steve Cruz ha ayudado a nuestra familia dos veces. Siempre honesto, siempre rapido, y nunca nos juzgo. Eso es raro en esta industria.' : "Steve Cruz has helped our family twice now. Always honest, always fast, and he never judged us. That's rare in this industry.",
      name: 'James & Linda W.', role: 'Fifth Ward, TX', initials: 'JW' },
    { text: lang === 'es' ? 'No sabia nada sobre fianzas. Steve me explico todo paso a paso por telefono. Mi hijo fue liberado esa misma noche. Dios los bendiga.' : "I didn't know anything about bail bonds. Steve walked me through the entire process step by step over the phone. My son was released that same night. God bless them.",
      name: 'Patricia D.', role: 'Greenspoint, TX', initials: 'PD' },
    { text: lang === 'es' ? 'Estaba aterrorizada cuando arrestaron a mi hermano. Lone Star Bonding hizo que todo fuera rapido e indoloro. Me mantuvieron informada en cada paso. Los recomiendo al 100%.' : "I was terrified when my brother got arrested. Lone Star Bonding made the whole thing quick and painless. They kept me informed every step of the way. 100% recommend.",
      name: 'Ashley R.', role: 'Alief, TX', initials: 'AR' },
    { text: lang === 'es' ? 'Tres decadas de experiencia se notan. Otros fiadores me daban vueltas, pero Steve fue directo y me dijo exactamente cuanto costaria y cuanto tardaria. Sin sorpresas.' : "Three decades of experience shows. Other bondsmen gave me the runaround but Steve was straight up and told me exactly what it would cost and how long it would take. No surprises.",
      name: 'Dwayne M.', role: 'Third Ward, TX', initials: 'DM' },
  ];

  const marqueeItems = [
    'TRUSTED BY HOUSTON',
    'LICENSE #74629',
    '30+ YEARS OF SERVICE',
    '24/7 AVAILABILITY',
    'SE HABLA ESPANOL',
    'HARRIS COUNTY',
    'STEVE CRUZ',
    'FAST RELEASE',
    'FAMILY OWNED',
    'TRUSTED BY HOUSTON',
    'LICENSE #74629',
    '30+ YEARS OF SERVICE',
  ];

  return (
    <div ref={gsapScopeRef}>
      <Helmet>
        <title>Lone Star Bonding Co. | Houston's #1 Trusted 24/7 Bail Bonds | License #74629 | 30+ Years</title>
        <meta name="description" content="Need bail bonds in Houston? Lone Star Bonding Co. provides fast, confidential 24/7 bail bond service across Harris County. Licensed bondsman Steve Cruz - 30+ years experience. Call (713) 228-8800 now." />
        <meta name="keywords" content="houston bail bonds, bail bonds near me, 24/7 bail bonds houston, harris county bail bonds, bail bondsman houston tx, steve cruz bail bonds, sunnyside bail bonds, fifth ward bail bonds, sharpstown bail bonds, northside houston bail bonds, fast bail bonds, affordable bail bonds houston, spanish bail bonds houston, fianzas houston" />
        <link rel="canonical" href="https://lonestarbonding.com" />
        <meta property="og:title" content="Lone Star Bonding Co. | Houston's Trusted 24/7 Bail Bond Service" />
        <meta property="og:description" content="Fast, confidential bail bond service in Houston TX. 30+ years experience. Licensed #74629. Call (713) 228-8800." />
        <meta property="og:type" content="website" />
      </Helmet>
      <StructuredData />

      {/* ═══ EMERGENCY BANNER ═══ */}
      <div className="emergency-banner" data-testid="emergency-banner">
        <span className="emergency-banner__text">
          {lang === 'es' ? 'SERVICIO DE EMERGENCIA 24/7' : '24/7 EMERGENCY BAIL SERVICE'}
        </span>
        <a href="tel:7132288800" className="emergency-banner__phone" onClick={() => trackEvent('call_click')} data-testid="banner-call-btn">
          (713) 228-8800
        </a>
        <div className="lang-toggle" data-testid="lang-toggle">
          <button className={`lang-toggle__btn ${lang === 'en' ? 'lang-toggle__btn--active' : ''}`} onClick={() => switchLang('en')}>EN</button>
          <button className={`lang-toggle__btn ${lang === 'es' ? 'lang-toggle__btn--active' : ''}`} onClick={() => switchLang('es')}>ES</button>
        </div>
      </div>

      {/* ═══ NAVIGATION ═══ */}
      <nav className={`cinematic-nav ${navScrolled ? 'cinematic-nav--scrolled' : ''}`} data-testid="main-nav">
        <div className="nav-inner">
          <Link to="/" className="nav-logo" data-testid="nav-logo">
            <img src="https://customer-assets.emergentagent.com/job_bond-app-hub/artifacts/95cp39mf_LONESTAR_BONDINGLOGO2.jpg"
              alt="Lone Star Bonding Co - Houston Bail Bonds" className="nav-logo__img" />
            <div className="nav-logo__text">
              <span className="nav-logo__name">LONE STAR BONDING</span>
              <span className="nav-logo__license">LICENSE #74629</span>
            </div>
          </Link>
          <div className={`nav-links ${mobileMenu ? 'nav-links--open' : ''}`}>
            <Link to="/" className="nav-link" onClick={() => setMobileMenu(false)}>{lang === 'es' ? 'INICIO' : 'HOME'}</Link>
            <Link to="/about" className="nav-link" onClick={() => setMobileMenu(false)}>{lang === 'es' ? 'NOSOTROS' : 'ABOUT'}</Link>
            <Link to="/services" className="nav-link" onClick={() => setMobileMenu(false)}>{lang === 'es' ? 'SERVICIOS' : 'SERVICES'}</Link>
            <Link to="/service-areas" className="nav-link" onClick={() => setMobileMenu(false)}>{lang === 'es' ? 'AREAS' : 'AREAS'}</Link>
            <Link to="/faq" className="nav-link" onClick={() => setMobileMenu(false)}>FAQ</Link>
            <Link to="/contact" className="nav-link" onClick={() => setMobileMenu(false)}>{lang === 'es' ? 'CONTACTO' : 'CONTACT'}</Link>
            <Link to="/intake" className="nav-link nav-link--forms" onClick={() => setMobileMenu(false)} data-testid="nav-forms">
              <FileText size={14} /> {lang === 'es' ? 'FORMULARIOS' : 'FORMS'}
            </Link>
            <Link to="/dashboard" className="nav-link nav-link--signin" onClick={() => setMobileMenu(false)} data-testid="nav-signin">
              <LogIn size={15} /> {lang === 'es' ? 'ENTRAR' : 'SIGN IN'}
            </Link>
            <a href="tel:7132288800" className="btn-emergency pulse-ring" onClick={() => trackEvent('call_click')}
              data-testid="nav-call-btn" style={{ padding: '0.6rem 1.5rem', fontSize: '1rem' }}>
              <Phone size={18} /> {lang === 'es' ? 'LLAMAR' : 'CALL NOW'}
            </a>
          </div>
          <button className="nav-mobile-toggle" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
            {mobileMenu ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="hero" ref={heroRef} data-testid="hero-section">
        <motion.div className="hero__bg" style={{ y: heroY, scale: heroScale }}>
          <img src="https://images.pexels.com/photos/9556461/pexels-photo-9556461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
            alt="Houston skyline at night" loading="eager" />
        </motion.div>
        <div className="hero__overlay" />
        <Particles />

        <motion.div className="hero__content" style={{ opacity: heroOpacity }}>
          <Reveal>
            <div className="hero__badge float-badge">
              <Star size={14} style={{ color: 'var(--astros-orange)' }} />
              <span className="hero__badge-text">
                {lang === 'es' ? 'MAS DE 30 ANOS DE SERVICIO' : 'OVER 30 YEARS OF SERVICE'}
              </span>
              <Star size={14} style={{ color: 'var(--astros-orange)' }} />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <h1 className="hero__title" data-testid="hero-title">
              LONE STAR
              <span className="hero__title-accent">BONDING CO.</span>
            </h1>
          </Reveal>

          <Reveal delay={0.25}>
            <p className="hero__subtitle">
              {lang === 'es'
                ? 'El servicio de fianzas mas confiable de Houston. Rapido, confidencial, disponible las 24 horas del dia, los 7 dias de la semana.'
                : "Houston's most trusted bail bond service. Fast, confidential, available 24 hours a day, 7 days a week."}
            </p>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="hero__license" data-testid="hero-license">
              {lang === 'es' ? 'LICENCIA NO.' : 'LICENSE NO.'} <span className="text-shimmer" style={{ display: 'inline' }}>74629</span> — STEVE CRUZ
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="hero__ctas">
              <a href="tel:7132288800" className="btn-emergency pulse-ring" onClick={() => trackEvent('call_click')} data-testid="hero-call-btn">
                <Phone size={22} />
                {lang === 'es' ? 'LLAMAR AHORA — 24/7' : 'CALL NOW — 24/7'}
              </a>
              <Link to="/how-bail-works" className="btn-ghost" data-testid="hero-learn-btn">
                {lang === 'es' ? 'COMO FUNCIONA' : 'HOW BAIL WORKS'}
                <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </motion.div>
      </section>

      {/* ═══ MARQUEE ═══ */}
      <Marquee items={marqueeItems} />

      {/* ═══ STATS BAR (Animated Counters) ═══ */}
      <section style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-dim)' }}>
        <div className="stats-row" data-testid="stats-bar">
          {[
            { value: '30+', label: lang === 'es' ? 'ANOS DE SERVICIO' : 'YEARS OF SERVICE' },
            { value: '24/7', label: lang === 'es' ? 'DISPONIBILIDAD' : 'AVAILABILITY' },
            { value: '#74629', label: lang === 'es' ? 'LICENCIA TEXAS' : 'TEXAS LICENSE' },
            { value: '10K+', label: lang === 'es' ? 'CLIENTES AYUDADOS' : 'CLIENTS HELPED' },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="stat-item">
                <div className="stat-item__value">
                  <Counter value={s.value} />
                </div>
                <div className="stat-item__label">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ GLOW DIVIDER ═══ */}
      <div className="glow-divider" />

      {/* ═══ WHY CHOOSE US ═══ */}
      <section style={{ padding: '7rem 2rem', background: 'var(--void)' }} data-testid="why-us-section">
        <Reveal>
          <div className="section-header">
            <span className="section-header__label">{lang === 'es' ? 'POR QUE ELEGIRNOS' : 'WHY CHOOSE US'}</span>
            <h2 className="section-header__title">{lang === 'es' ? 'AUTORIDAD COMPROBADA' : 'PROVEN AUTHORITY'}</h2>
            <p className="section-header__subtitle">
              {lang === 'es' ? 'Mas de tres decadas sirviendo a Houston con integridad, velocidad y compromiso inquebrantable.' : 'Over three decades serving Houston with integrity, speed, and unwavering commitment.'}
            </p>
          </div>
        </Reveal>
        <div className="authority__grid">
          {[
            { icon: <Clock size={28} />, title: lang === 'es' ? 'DISPONIBLE 24/7' : '24/7 AVAILABILITY', text: lang === 'es' ? 'Nunca cerramos. Llame a cualquier hora del dia o la noche, fines de semana y dias festivos.' : 'We never close. Call us anytime, day or night, weekends and holidays. Someone will always answer.' },
            { icon: <Shield size={28} />, title: lang === 'es' ? 'LICENCIA Y CONFIANZA' : 'LICENSED & TRUSTED', text: lang === 'es' ? 'Totalmente licenciado (No. 74629) y asegurado en Texas. Steve Cruz ha servido a Houston con integridad por mas de 30 anos.' : 'Fully licensed (No. 74629) and bonded in Texas. Steve Cruz has served Houston with integrity for over 30 years.' },
            { icon: <Award size={28} />, title: lang === 'es' ? 'LIBERACION RAPIDA' : 'FAST RELEASE', text: lang === 'es' ? 'La mayoria de los casos se procesan en horas, no dias.' : 'Most cases are processed within hours, not days.' },
            { icon: <Users size={28} />, title: lang === 'es' ? 'APOYO BILINGUE' : 'BILINGUAL SUPPORT', text: lang === 'es' ? 'Servicio completo en ingles y espanol. Hablamos su idioma.' : 'Full service in English and Spanish. We speak your language.' },
            { icon: <Scale size={28} />, title: lang === 'es' ? '30+ ANOS' : '30+ YEARS', text: lang === 'es' ? 'Navegando el sistema de fianzas del Condado de Harris desde 1990.' : 'Navigating Harris County bail system since 1990. We know the courts, the jails, and the process.' },
            { icon: <MapPin size={28} />, title: lang === 'es' ? 'LOCAL DE HOUSTON' : 'HOUSTON LOCAL', text: lang === 'es' ? 'Nacido y criado en Houston. Somos sus vecinos.' : 'Born and raised in Houston. We are your neighbors, and we care about this community like family.' },
          ].map((card, i) => (
            <div key={i} className="authority-card" data-testid={`authority-card-${i}`}>
              <div className="authority-card__icon">{card.icon}</div>
              <h3 className="authority-card__title">{card.title}</h3>
              <p className="authority-card__text">{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ GLOW DIVIDER ═══ */}
      <div className="glow-divider" />

      {/* ═══ ABOUT STEVE CRUZ ═══ */}
      <section style={{ padding: '7rem 2rem', background: 'var(--surface)' }} data-testid="about-section">
        <div className="about-grid-2col" style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
          <Reveal direction="left">
            <div>
              <span className="section-header__label" style={{ textAlign: 'left' }}>{lang === 'es' ? 'CONOZCA A STEVE CRUZ' : 'MEET STEVE CRUZ'}</span>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 3.5rem)', color: 'white', marginBottom: '1.5rem', textTransform: 'uppercase' }}>
                {lang === 'es' ? 'SU FIADOR DE CONFIANZA EN HOUSTON' : 'YOUR TRUSTED HOUSTON BAIL BONDSMAN'}
              </h2>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                {lang === 'es' ? 'Steve Cruz y Lone Star Bonding Co. han servido a la comunidad de Houston por mas de 30 anos, ayudando a las familias a actuar rapido cuando el tiempo importa.' : 'Steve Cruz and Lone Star Bonding Co. have served the Houston community for more than 30 years, helping families move fast when time matters most.'}
              </p>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>
                {lang === 'es' ? 'Entendemos que recibir una llamada sobre un ser querido en la carcel es estresante y confuso. Por eso estamos disponibles 24/7.' : 'We understand getting a call about a loved one in jail is stressful and confusing. That is why we are available 24/7 to answer your questions and get your family member released as quickly as possible.'}
              </p>
              <p style={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                {lang === 'es' ? 'Nuestro compromiso es simple: tratar a cada cliente con respeto, actuar rapido y brindar un servicio honesto.' : 'Our commitment is simple: treat every client with respect, move quickly, and provide honest, transparent service at fair rates.'}
              </p>
              <Link to="/about" className="btn-ghost" data-testid="about-learn-more">
                {lang === 'es' ? 'CONOCER MAS' : 'LEARN MORE'} <ChevronRight size={18} />
              </Link>
            </div>
          </Reveal>
          <Reveal direction="right">
            <div style={{ padding: '2.5rem', background: 'rgba(191, 10, 48, 0.06)', border: '1px solid rgba(191, 10, 48, 0.2)', position: 'relative' }}>
              <div style={{ position: 'absolute', top: -1, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--astros-orange), var(--texas-red))' }} />
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--astros-orange)', letterSpacing: '0.1em', marginBottom: '1.5rem' }}>
                {lang === 'es' ? 'NUESTRAS CREDENCIALES' : 'OUR CREDENTIALS'}
              </h3>
              {[
                { label: lang === 'es' ? 'Fiador Licenciado' : 'Licensed Bail Bondsman', detail: 'License No. 74629' },
                { label: lang === 'es' ? '30+ Anos de Experiencia' : '30+ Years Experience', detail: 'Harris County' },
                { label: lang === 'es' ? 'Servicio Bilingue' : 'Bilingual Service', detail: lang === 'es' ? 'Ingles y Espanol' : 'English & Spanish' },
                { label: lang === 'es' ? 'Propiedad Familiar' : 'Family Owned & Operated', detail: 'Since 1990' },
                { label: lang === 'es' ? 'Miembro' : 'Member', detail: 'Texas Bail Bond Association' },
                { label: lang === 'es' ? 'Sirviendo' : 'Serving', detail: lang === 'es' ? 'Todas las Carceles del Condado Harris' : 'All Harris County Jails' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.85rem 0', borderBottom: i < 5 ? '1px solid rgba(51, 65, 85, 0.3)' : 'none' }}>
                  <div style={{ width: 8, height: 8, background: 'var(--astros-orange)', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>{item.label}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section style={{ padding: '7rem 2rem', background: 'var(--void)' }} data-testid="testimonials-section">
        <Reveal>
          <div className="section-header">
            <span className="section-header__label">{lang === 'es' ? 'TESTIMONIOS' : 'TESTIMONIALS'}</span>
            <h2 className="section-header__title">{lang === 'es' ? 'LO QUE DICEN DE NOSOTROS' : 'WHAT PEOPLE SAY'}</h2>
            <p className="section-header__subtitle">
              {lang === 'es' ? 'Las familias de Houston confian en nosotros cuando mas lo necesitan.' : 'Houston families trust us when they need it most.'}
            </p>
          </div>
        </Reveal>
        <div className="testimonial-grid">
          {testimonials.map((t, i) => (
            <div key={i} className="testimonial-card" data-testid={`testimonial-${i}`}>
              <div className="testimonial-card__stars">
                {Array.from({ length: 5 }).map((_, j) => <Star key={j} size={16} />)}
              </div>
              <p className="testimonial-card__text">{t.text}</p>
              <div className="testimonial-card__author">
                <div className="testimonial-card__avatar">{t.initials}</div>
                <div>
                  <div className="testimonial-card__name">{t.name}</div>
                  <div className="testimonial-card__role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ GLOW DIVIDER ═══ */}
      <div className="glow-divider" />

      {/* ═══ SERVICE AREAS ═══ */}
      <section style={{ padding: '7rem 2rem', background: 'var(--surface)' }} data-testid="service-areas-section">
        <Reveal>
          <div className="section-header">
            <span className="section-header__label">{lang === 'es' ? 'AREAS DE SERVICIO' : 'SERVICE AREAS'}</span>
            <h2 className="section-header__title">{lang === 'es' ? 'SIRVIENDO TODO HOUSTON' : 'SERVING ALL OF HOUSTON'}</h2>
            <p className="section-header__subtitle">
              {lang === 'es' ? 'Cobertura de fianzas 24/7 para todas las comunidades del area de Houston.' : '24/7 bail bond coverage for all Houston area jails and communities across Harris County and beyond.'}
            </p>
          </div>
        </Reveal>
        <div className="areas-grid">
          {serviceAreas.map((area, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="area-card" data-testid={`area-card-${i}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <MapPin size={18} style={{ color: 'var(--astros-orange)', flexShrink: 0 }} />
                  <h3 className="area-card__name">{area.name}</h3>
                </div>
                <p className="area-card__detail">{area.desc}</p>
                <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>ZIP: {area.zip}</div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to="/service-areas" className="btn-ghost" data-testid="view-all-areas-btn">
              {lang === 'es' ? 'VER TODAS LAS AREAS' : 'VIEW ALL SERVICE AREAS'} <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ═══ EMERGENCY CTA ═══ */}
      <section className="cta-section" data-testid="cta-section">
        <div className="cta-section__bg">
          <img src="https://images.unsplash.com/photo-1738130399737-f39ef0637ddc?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85" alt="Emergency" loading="lazy" />
        </div>
        <div className="cta-section__overlay" />
        <div className="cta-section__content">
          <Reveal>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(3rem, 8vw, 7rem)', color: 'white', textTransform: 'uppercase', marginBottom: '1rem', textShadow: '0 0 80px rgba(191,10,48,0.5)' }}>
              {lang === 'es' ? 'NO ESPERE' : "DON'T WAIT"}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p style={{ fontSize: '1.3rem', color: 'var(--astros-silver)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
              {lang === 'es' ? 'Alguien esta esperando para ayudarle ahora mismo. Cada minuto cuenta.' : 'Someone is standing by to help you right now. Every minute counts.'}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <a href="tel:7132288800" className="btn-emergency pulse-ring" onClick={() => trackEvent('call_click')}
              data-testid="cta-call-btn" style={{ fontSize: '1.6rem', padding: '1.5rem 3.5rem' }}>
              <Phone size={28} />
              (713) 228-8800
            </a>
          </Reveal>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="cinematic-footer" data-testid="footer">
        <div className="footer-inner">
          <div>
            <img src="https://customer-assets.emergentagent.com/job_bond-app-hub/artifacts/jwllno7h_LINESTARBONDING-LOGO1.jpg" alt="Lone Star Bonding Co." className="footer-brand__logo" />
            <p className="footer-brand__desc">{lang === 'es' ? 'Sirviendo a Houston y el Condado de Harris con servicios de fianzas confiables por mas de 30 anos. Licencia #74629.' : 'Serving Houston and Harris County with trusted bail bond services for over 30 years. License #74629.'}</p>
          </div>
          <div>
            <h4 className="footer-col__title">{lang === 'es' ? 'NAVEGACION' : 'NAVIGATION'}</h4>
            <Link to="/" className="footer-col__link">{lang === 'es' ? 'Inicio' : 'Home'}</Link>
            <Link to="/about" className="footer-col__link">{lang === 'es' ? 'Nosotros' : 'About Us'}</Link>
            <Link to="/services" className="footer-col__link">{lang === 'es' ? 'Servicios' : 'Services'}</Link>
            <Link to="/how-bail-works" className="footer-col__link">{lang === 'es' ? 'Como Funciona' : 'How Bail Works'}</Link>
            <Link to="/faq" className="footer-col__link">FAQ</Link>
          </div>
          <div>
            <h4 className="footer-col__title">{lang === 'es' ? 'RECURSOS' : 'RESOURCES'}</h4>
            <Link to="/intake" className="footer-col__link">{lang === 'es' ? 'Formulario de Solicitud' : 'Application Form'}</Link>
            <Link to="/dashboard" className="footer-col__link">{lang === 'es' ? 'Portal del Personal' : 'Staff Portal (Sign In)'}</Link>
            <Link to="/service-areas" className="footer-col__link">{lang === 'es' ? 'Areas de Servicio' : 'Service Areas'}</Link>
            <Link to="/contact" className="footer-col__link">{lang === 'es' ? 'Contacto' : 'Contact Us'}</Link>
          </div>
          <div>
            <h4 className="footer-col__title">{lang === 'es' ? 'CONTACTO' : 'CONTACT'}</h4>
            <a href="tel:7132288800" className="footer-col__link" style={{ color: 'var(--astros-orange)', fontWeight: 700 }}>(713) 228-8800</a>
            <span className="footer-col__link">1420 Washington Ave</span>
            <span className="footer-col__link">Houston, TX 77002</span>
            <span className="footer-col__link" style={{ color: 'var(--astros-orange)', fontWeight: 600, marginTop: '0.5rem' }}>{lang === 'es' ? 'Disponible 24/7' : 'Available 24/7'}</span>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-bottom__copy">&copy; {new Date().getFullYear()} Lone Star Bonding Co. All rights reserved. License #74629.</p>
          <p className="footer-bottom__credit">Developed by James Hudson @ Texas IT Nerds | Protected by TITN Aegis Security</p>
        </div>
      </footer>
    </div>
  );
};

export default CinematicHome;
